import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CompletedDTO,
  EscalationsDTO,
  FlowAPipelineDTO,
  FlowBPipelineDTO,
  FlowMonitorApplicationsDTO,
  FlowMonitorFilterOptionsDTO,
  FlowMonitorFilters,
  FlowMonitorDrillDown,
  OverviewKpiDTO,
  ActiveOfficerDTO,
} from '../models/flow-monitor.model';
import { FlowMonitorService } from '../tokens/flow-monitor.token';
import { API_PATHS } from '../config/api-paths';

/**
 * EM-111 — REST-backed FlowMonitor service.
 *
 * Calls: GET /services/embedded/api/flow-monitor/onboarding/{tab}
 * Authenticated via authInterceptor (signed-in KYC officer / CS bearer token).
 * REST-only per Rule 07/08. No mock fallback.
 */
@Injectable()
export class FlowMonitorRestService implements FlowMonitorService {
  private readonly http = inject(HttpClient);
  private readonly base = `${API_PATHS.EMBEDDED_API_BASE}/flow-monitor/onboarding`;

  private buildParams(filters?: FlowMonitorFilters): HttpParams {
    let p = new HttpParams();
    if (!filters) return p;
    if (filters.entityType) p = p.set('entityType', filters.entityType);
    if (filters.priority)   p = p.set('priority', filters.priority);
    if (filters.flow)       p = p.set('flow', filters.flow);
    if (filters.owner)      p = p.set('owner', filters.owner);
    if (filters.status)     p = p.set('status', filters.status);
    if (filters.tag)        p = p.set('tag', filters.tag);
    return p;
  }

  getOverview(filters?: FlowMonitorFilters): Observable<OverviewKpiDTO> {
    return this.http.get<OverviewKpiDTO>(`${this.base}/overview`, {
      params: this.buildParams(filters),
    });
  }

  getFlowA(filters?: FlowMonitorFilters): Observable<FlowAPipelineDTO> {
    return this.http.get<FlowAPipelineDTO>(`${this.base}/flow-a`, {
      params: this.buildParams(filters),
    });
  }

  getFlowB(filters?: FlowMonitorFilters): Observable<FlowBPipelineDTO> {
    return this.http.get<FlowBPipelineDTO>(`${this.base}/flow-b`, {
      params: this.buildParams(filters),
    });
  }

  getEscalations(filters?: FlowMonitorFilters): Observable<EscalationsDTO> {
    return this.http.get<EscalationsDTO>(`${this.base}/escalations`, {
      params: this.buildParams(filters),
    });
  }

  getCompleted(from?: string, to?: string, filters?: FlowMonitorFilters): Observable<CompletedDTO> {
    let p = this.buildParams(filters);
    if (from) p = p.set('from', from);
    if (to)   p = p.set('to', to);
    return this.http.get<CompletedDTO>(`${this.base}/completed`, { params: p });
  }

  getFilterOptions(): Observable<FlowMonitorFilterOptionsDTO> {
    return this.http.get<FlowMonitorFilterOptionsDTO>(`${this.base}/filters`);
  }

  /**
   * Single-page Flow Monitor — BRS V8 mockup. Returns pipeline stage counts +
   * the full filtered application list in one round trip.
   */
  getApplications(filters?: FlowMonitorFilters): Observable<FlowMonitorApplicationsDTO> {
    return this.http.get<FlowMonitorApplicationsDTO>(`${this.base}/applications`, {
      params: this.buildParams(filters),
    });
  }

  /** GET /api/tasks/officers/active — active KYC officers for Transfer modal (unified TaskResource, Rule 14). */
  getActiveOfficers(): Observable<ActiveOfficerDTO[]> {
    return this.http.get<ActiveOfficerDTO[]>(`${API_PATHS.EMBEDDED_API_BASE}/tasks/officers/active`);
  }

  /** GET /api/flow-monitor/onboarding/{id}/drill-down — drill-down detail for a single application */
  getDrillDown(applicationId: number): Observable<FlowMonitorDrillDown> {
    return this.http.get<FlowMonitorDrillDown>(`${this.base}/${applicationId}/drill-down`);
  }
}
